import React, { useState, useEffect } from 'react';
import KanbanBoard from './components/KanbanBoard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import CustomerDisplay from './components/CustomerDisplay';
import Settings from './components/Settings';
import RefreshButton from './components/RefreshButton';
import DateFilter from './components/DateFilter';
import OrderNotifier from './components/OrderNotifier';
import { cn } from './lib/utils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { useSettings } from './context/SettingsContext';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('kanban');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isTestData } = useSettings();

  useEffect(() => {
    try {
      if (screen.orientation && screen.orientation.lock) {
        screen.orientation.lock('portrait').catch(() => {});
      }
    } catch (e) {}
  }, []);

  const DesktopNavButton = ({ view, icon, title }) => {
    const isActive = currentView === view;
    return (
      <button
        onClick={() => setCurrentView(view)}
        className={cn(
          "h-11 px-4 rounded-md transition-all font-bold flex items-center gap-3 justify-center",
          isActive 
            ? "bg-yellow-400 text-black shadow-lg scale-105" 
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white border border-transparent hover:border-zinc-600"
        )}
        title={title}
      >
        <SafeIcon icon={icon} size={20} />
      </button>
    );
  };

  const MobileMenuButton = ({ view, icon, title }) => {
    const isActive = currentView === view;
    return (
      <button
        onClick={() => {
          setCurrentView(view);
          setIsMobileMenuOpen(false);
        }}
        className={cn(
          "flex items-center gap-4 px-6 py-4 rounded-xl font-black uppercase tracking-widest transition-all",
          isActive 
            ? "bg-yellow-400 text-black shadow-lg" 
            : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-white"
        )}
      >
        <SafeIcon icon={icon} size={24} />
        <span>{title}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-black text-gray-100 font-sans selection:bg-yellow-400 selection:text-black">
      <OrderNotifier />
      
      {/* Top Header - учитывает Safe Area сверху */}
      <header 
        className="flex-none px-4 lg:px-6 flex items-center justify-between z-30 border-b border-zinc-800 bg-zinc-950"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)',
          paddingBottom: '0.75rem'
        }}
      >
        <div className="flex items-center gap-4">
          <img 
            src="https://static.tildacdn.biz/tild6631-3039-4338-a239-393362643464/logo_heroes_descript.svg" 
            alt="Heroes Playbar" 
            className="h-10 lg:h-14 object-contain cursor-pointer hover:opacity-80 transition-opacity" 
            onClick={() => {
              setCurrentView('kanban');
              window.dispatchEvent(new CustomEvent('kanban-set-status', { detail: { status: 'Received' } }));
            }}
          />
          {isTestData && (
            <div className="hidden sm:block bg-yellow-400 text-black text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter animate-pulse">
              Test Data
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 lg:gap-4">
          {currentView === 'kanban' && (
            <div className="flex items-center gap-2">
              <DateFilter selectedDate={selectedDate} onDateChange={setSelectedDate} />
              <RefreshButton />
              <div className="hidden lg:block w-px h-8 bg-zinc-800 ml-2"></div>
            </div>
          )}

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 bg-zinc-900 border border-zinc-800 p-1 rounded-lg">
            <DesktopNavButton view="kanban" icon={FiIcons.FiLayout} title="Kanban Board" />
            <DesktopNavButton view="customer" icon={FiIcons.FiMonitor} title="Customer Display" />
            <DesktopNavButton view="analytics" icon={FiIcons.FiPieChart} title="Analytics Dashboard" />
            <DesktopNavButton view="settings" icon={FiIcons.FiSettings} title="Settings" />
          </div>

          {/* Mobile Burger Menu Button */}
          <button 
            className="lg:hidden h-11 w-11 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 text-yellow-400 rounded-md transition-colors border border-zinc-700 shrink-0"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <SafeIcon icon={FiIcons.FiMenu} size={20} />
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 min-h-0 overflow-hidden p-4 lg:p-6 relative">
        {currentView === 'kanban' && <KanbanBoard selectedDate={selectedDate} />}
        {currentView === 'analytics' && <AnalyticsDashboard />}
        {currentView === 'customer' && <CustomerDisplay selectedDate={selectedDate} />}
        {currentView === 'settings' && <Settings />}
      </main>

      {/* Mobile Sidebar Navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-zinc-950 border-l border-zinc-800 z-50 lg:hidden flex flex-col shadow-2xl"
              style={{
                paddingTop: 'calc(env(safe-area-inset-top) + 1rem)',
                paddingBottom: 'env(safe-area-inset-bottom)'
              }}
            >
              <div className="px-6 py-2 flex justify-between items-center mb-6">
                <span className="font-black uppercase tracking-widest text-zinc-500 text-sm">Menu</span>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="p-2 bg-zinc-900 rounded-full text-zinc-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiIcons.FiX} size={24} />
                </button>
              </div>
              
              <div className="flex-1 flex flex-col gap-3 px-4">
                <MobileMenuButton view="kanban" icon={FiIcons.FiLayout} title="Orders" />
                <MobileMenuButton view="customer" icon={FiIcons.FiMonitor} title="Display" />
                <MobileMenuButton view="analytics" icon={FiIcons.FiPieChart} title="Analytics" />
                <MobileMenuButton view="settings" icon={FiIcons.FiSettings} title="Settings" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;